import { useQuery } from '@apollo/client/react';

import { GET_MAP } from '@atom/graph/map';
import { EMPTY_MAP_DATA } from '@atom/map';
import { MapData, MapInput } from '@atom/types/map';

import { MapLayersState } from './layers/mapLayersReducer';

const MAX_ZOOM = 20;

type UseMapDataParams = {
  coordinates: string | null;
  zoom: number | null;
};

const buildInput = (
  layers: MapLayersState,
  { coordinates, zoom }: UseMapDataParams,
): MapInput | null => {
  if (zoom === null || !coordinates) {
    return null;
  }

  const includeWorkOrders = Boolean(
    layers.work &&
    (layers.workStatusIds.size ||
      layers.workTemplateIds.size ||
      layers.workTemplateFolderIds.size),
  );

  const includeAssets = Boolean(layers.assets && layers.assetSchemas.size);

  return {
    coordinates: JSON.parse(coordinates) as number[][][],
    zoomLevel: Math.min(zoom, MAX_ZOOM),
    clusterDensity: layers.clusterDensity,
    includeUsers: layers.users,
    includeWorkOrders,
    includeAssets,
    schemaIds: Array.from(layers.assetSchemas),
    statusIds: Array.from(layers.workStatusIds),
    workTemplateIds: Array.from(layers.workTemplateIds),
    workTemplateFolderIds: Array.from(layers.workTemplateFolderIds),
    categoryIds: Array.from(layers.categoryIds),
  };
};

const useMapData = (
  layers: MapLayersState,
  params: UseMapDataParams,
): [MapData, boolean] => {
  const input = buildInput(layers, params);

  const { data, previousData, loading } = useQuery(GET_MAP, {
    variables: input ? { input } : undefined,
    skip: !input,
    fetchPolicy: 'network-only',
  });

  return [data?.map ?? previousData?.map ?? EMPTY_MAP_DATA, loading];
};

export default useMapData;
